import type { NavigateFunction } from "react-router-dom";
import { OpenBrowser } from "src/hybrid/components/Browser";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import type { QuestionnaireField } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import { tracker } from "src/state/state";
import { TrackEvent } from "src/state/Track/TrackCubit";
import {
  type AppPopup,
  AppPopupTypesSet,
  AppQueryPopupsController
} from "../AppQueryPopups/AppQueryPopupsBloc";
import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";

interface HandleButtonPressOptions {
  field: QuestionnaireField;
  formId: string;
  parsedLink: string;
  replacePlaceholders: (value: string) => string | undefined;
  navigate: NavigateFunction;
}

function trackLinkButtonClick(
  formId: string,
  field: QuestionnaireField,
  linkType: "popup" | "internal" | "external",
  url: string
): void {
  tracker.track(TrackEvent.QuestionnaireLinkButtonClicked, {
    data: {
      formId,
      fieldRef: field.ref,
      fieldId: field.id,
      fieldTitle: field.title,
      linkType,
      url
    }
  });
}

export default function handleQuestionnaireButtonPress({
  field,
  formId,
  parsedLink,
  replacePlaceholders,
  navigate
}: HandleButtonPressOptions): void {
  const buttonPopup = field.properties?.button_popup;
  if (buttonPopup) {
    const parsedButtonPopup = replacePlaceholders(buttonPopup) ?? buttonPopup;
    const urlParsed = new URLSearchParams(parsedButtonPopup);
    const data = Object.fromEntries(urlParsed.entries());
    const isValidPopupType = AppPopupTypesSet.has(data.type as AppPopup);

    if (!isValidPopupType) {
      reportErrorSentry(
        new Error(`Invalid popup type: ${data.type} for field ${field.id}`),
        {
          extra: {
            fieldId: field.id
          }
        }
      );
      return;
    }

    BlockingLoadingOverlayController.startLoading({
      bg: "transparent"
    });
    setTimeout(() => {
      AppQueryPopupsController.openPopup(data.type as AppPopup, {
        additionalParameters: {
          url: data.url,
          title: translate("link.scheduleAppointment")
        }
      });
      BlockingLoadingOverlayController.endLoading();
    }, 500);

    trackLinkButtonClick(formId, field, "popup", data.url);

    return;
  }

  const isExternal = parsedLink.startsWith("http");

  if (parsedLink && !isExternal && parsedLink.startsWith("/app")) {
    trackLinkButtonClick(formId, field, "internal", parsedLink);
    navigate(parsedLink);
    return;
  }

  if (parsedLink && isExternal) {
    trackLinkButtonClick(formId, field, "external", parsedLink);
    void OpenBrowser(parsedLink, {
      presentationStyle: "fullscreen",
      useBaseUrl: false
    });
    AppQueryPopupsController.closePopup();
    return;
  }
}
